import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ClipboardCheck, Store, LogOut } from "lucide-react";
import { logout, selectUser } from "../../redux/slices/authSlice";

/**
 * Lets head promoters choose their session mode after login.
 *
 * - Promoter: Use the promoter app.
 * - Approver: Manage stock requests in the admin section.
 *
 * This only controls navigation, not access permissions.
 */

export default function ModeChooser() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Only head promoters have a choice; anyone else goes to their normal home.
  if (!(user?.role === "promoter" && user?.is_head_promoter)) {
    return <Navigate to="/promoter" replace />;
  }

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        padding: "24px",
      }}
    >
      <h1 style={{ fontSize: "22px", color: "#111827", marginBottom: "4px" }}>
        Welcome, {user.full_name || "Head of Promoter"}
      </h1>
      <p style={{ color: "#6b7280", marginBottom: "28px" }}>
        How would you like to continue?
      </p>

      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center" }}>
        <button
          type="button"
          onClick={() => navigate("/promoter")}
          style={cardStyle}
        >
          <Store size={40} color="#C8102E" />
          <span style={titleStyle}>Check in as Promoter</span>
          <span style={descStyle}>
            Check in, record sales, and request stock (including roadshow).
          </span>
        </button>

        <button
          type="button"
          onClick={() => navigate("/admin")}
          style={cardStyle}
        >
          <ClipboardCheck size={40} color="#C8102E" />
          <span style={titleStyle}>Approver</span>
          <span style={descStyle}>
            Review, approve or reject stock requests and export transfer orders.
          </span>
        </button>
      </div>

      <button
        type="button"
        onClick={handleLogout}
        style={{
          marginTop: "28px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: "none",
          border: "none",
          color: "#6b7280",
          cursor: "pointer",
          fontSize: "14px",
        }}
      >
        <LogOut size={16} /> Log out
      </button>
    </div>
  );
}

const cardStyle = {
  width: "260px",
  minHeight: "190px",
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: "14px",
  padding: "28px 22px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  textAlign: "center",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const titleStyle = {
  fontSize: "17px",
  fontWeight: 700,
  color: "#111827",
};

const descStyle = {
  fontSize: "13px",
  color: "#6b7280",
  lineHeight: 1.5,
};
